import type { SelectedElement } from "schemas/generated/selectMode";
import type {
  IframeRpcChannelInputHandlers,
  IframeRpcChannelRemote,
} from "schemas/iframeRpcChannel";
import type {
  ParentToMiniappNotificationInputOf,
  ParentToMiniappRequestInputOf,
  ParentToMiniappRequestOutputOf,
} from "schemas/miniappRpc";

import { useEffect } from "react";

import { IframeRpcChannelUnsubscribedError } from "schemas/errors";
import {
  createIframeRpcChannel,
  createIframeRpcRemote,
} from "schemas/iframeRpcChannel";
import {
  MINIAPP_RPC_CHANNEL_ID,
  MINIAPP_RPC_CHANNEL_VERSION,
  miniappToParentMethodConfig,
  parentToMiniappMethodConfig,
} from "schemas/miniappRpc";

export type ParentToMiniappNotificationHandlers = Omit<
  IframeRpcChannelInputHandlers<typeof parentToMiniappMethodConfig>,
  "setPageAuditUiState" | "symbolicateSelectModeElement"
>;

// NOTE (Noah, 2026-05-12): Module-level handler pointers; the React hook
// sets these on mount and nulls them on unmount. Inbound void/valued
// messages are silently dropped until a `BedrockProvider` mounts.
let notificationHandlers: ParentToMiniappNotificationHandlers | null = null;
let symbolicateSelectModeElementHandler:
  | ((input: SelectedElement) => Promise<SelectedElement>)
  | null = null;
let setPageAuditUiStateHandler:
  | ((input: ParentToMiniappNotificationInputOf<"setPageAuditUiState">) => void)
  | null = null;

function createMiniappRpcChannel() {
  return createIframeRpcChannel({
    subscriberWindow: window,
    channelIdentifier: MINIAPP_RPC_CHANNEL_ID,
    version: MINIAPP_RPC_CHANNEL_VERSION,
    getTargetWindow: () => (window.parent === window ? null : window.parent),
    targetOrigin: "*",
    isExpectedMessage: (event) => event.source === window.parent,
    outgoingMethods: miniappToParentMethodConfig,
    incomingMethods: parentToMiniappMethodConfig,
    handlers: {
      setThemeCssVars: (data) => {
        notificationHandlers?.setThemeCssVars(data);
      },
      setThemeConfig: (data) => {
        notificationHandlers?.setThemeConfig(data);
      },
      setAgentUiState: (data) => {
        notificationHandlers?.setAgentUiState(data);
      },
      setCurrentRoute: (data) => {
        notificationHandlers?.setCurrentRoute(data);
      },
      setTabActive: (data) => {
        notificationHandlers?.setTabActive(data);
      },
      setPageAuditUiState: (data) => {
        setPageAuditUiStateHandler?.(data);
      },
      setCommandMenuState: (data) => {
        notificationHandlers?.setCommandMenuState(data);
      },
      restoreCommandMenuFocus: () => {
        notificationHandlers?.restoreCommandMenuFocus();
      },
      shortcutInvoked: (data) => {
        notificationHandlers?.shortcutInvoked(data);
      },
      selectModeRelayMessage: (data) => {
        notificationHandlers?.selectModeRelayMessage(data);
      },
      symbolicateSelectModeElement: (input) => {
        if (!symbolicateSelectModeElementHandler) {
          throw new IframeRpcChannelUnsubscribedError({
            method: "symbolicateSelectModeElement",
          });
        }
        return symbolicateSelectModeElementHandler(input);
      },
    },
  });
}

let channel: ReturnType<typeof createMiniappRpcChannel> | null = null;
let unsubscribe: (() => void) | null = null;

function getChannel() {
  if (typeof window === "undefined") {
    return null;
  }
  channel ??= createMiniappRpcChannel();
  unsubscribe ??= channel.subscribe();
  return channel;
}

export type ParentRemote = IframeRpcChannelRemote<
  typeof miniappToParentMethodConfig
>;

// NOTE (Noah, 2026-05-12): Always-available imperative API for miniapp ->
// parent calls. Safe to import from non-React modules. The window listener
// is installed lazily so importing this module in non-browser contexts
// (SSR, unit tests) doesn't touch `window`.
export const parentRemote = createIframeRpcRemote(
  getChannel,
  miniappToParentMethodConfig,
);

export type UseAgentUiParentInterfaceOptions = {
  notificationHandlers: ParentToMiniappNotificationHandlers;
};

/**
 * Miniapp-side RPC hook. Mirror of `useMiniappInterface` on the parent. Wires
 * inbound parent -> miniapp messages to the provided callbacks and returns
 * the imperative `remote` for outbound notifications. Only one
 * `BedrockProvider` should mount at a time; if multiple mount, the most
 * recent one wins (its handlers replace the previous ones).
 */
export function useAgentUiParentInterface({
  notificationHandlers: notificationHandlersProp,
}: UseAgentUiParentInterfaceOptions): {
  remote: ParentRemote;
} {
  // eslint-disable-next-line replo/no-use-effect -- Window-message subscription; no derivable replacement
  useEffect(() => {
    getChannel();
    notificationHandlers = notificationHandlersProp;
    return () => {
      notificationHandlers = null;
    };
  }, [notificationHandlersProp]);

  return { remote: parentRemote };
}

export function useSymbolicateSelectModeElementRequestHandler({
  handler,
}: {
  handler: (
    input: ParentToMiniappRequestInputOf<"symbolicateSelectModeElement">,
  ) => Promise<ParentToMiniappRequestOutputOf<"symbolicateSelectModeElement">>;
}) {
  // eslint-disable-next-line replo/no-use-effect -- NOTE (Noah, 2026-07-25): Registers the cross-window handler only while its preview is mounted.
  useEffect(() => {
    symbolicateSelectModeElementHandler = handler;
    return () => {
      symbolicateSelectModeElementHandler = null;
    };
  }, [handler]);
}

export function usePageAuditUiStateHandler({
  handler,
}: {
  handler: (
    input: ParentToMiniappNotificationInputOf<"setPageAuditUiState">,
  ) => void;
}) {
  // eslint-disable-next-line replo/no-use-effect -- Registers the cross-window page-audit handler while the website preview is mounted.
  useEffect(() => {
    setPageAuditUiStateHandler = handler;
    return () => {
      setPageAuditUiStateHandler = null;
    };
  }, [handler]);
}

// NOTE (Noah, 2026-05-12): Test helper for resetting module-level state
// between unit tests. Lets the listener re-install against a
// freshly-stubbed `window`. Not part of the public API.
export function __resetMiniappRpcForTests() {
  unsubscribe?.();
  unsubscribe = null;
  channel = null;
  notificationHandlers = null;
  symbolicateSelectModeElementHandler = null;
  setPageAuditUiStateHandler = null;
}
